import axios from 'axios';
import { getIntegrationStatusOnly } from './unifiedAuthService';
import { getMe } from './meService';
import { API_BASE_URL } from '../utils/config';

// getAdminTenants fetches available_tenants from GET /api/users/me/full (cached).
// This replaces the old standalone /api/integrations/admin-tenants endpoint.

// Re-export available_tenants in the shape IntegrationsPage expects.
export const getAdminTenants = async (_token, { forceRefresh = false } = {}) => {
  try {
    const me = await getMe({ forceRefresh });
    // me.available_tenants shape: [{ id, email, tenant_name, domain }]
    return me.available_tenants || [];
  } catch (error) {
    console.error('Failed to fetch admin tenants:', error);
    return [];
  }
};


const BASE_URL = API_BASE_URL;

// Backward compatibility wrapper - now uses unified service internally
export const getStatus = async (email, token, { forceRefresh = false } = {}) => {
  try {
    const integrationData = await getIntegrationStatusOnly(email, { forceRefresh });
    
    // If no integration data, return null (user needs to set up integration)
    if (!integrationData) {
      return null;
    }
    
    // Transform the data to match expected structure with is_configured field
    // Check if workflow fields are at root level or in workflow_status object
    const workflowImported = integrationData.workflow_imported || integrationData.workflow_status?.workflow_imported;
    const accountConfigured = integrationData.account_configured || integrationData.workflow_status?.account_configured;
    const workflowSaved = integrationData.workflow_saved || integrationData.workflow_status?.workflow_saved;
    
    // Integration is considered fully configured when:
    // 1. Admin case: all three workflow steps are complete (workflow_imported + account_configured + workflow_saved)
    // 2. User (non-admin) case: at least one service has both agent_id and skill_id set
    //
    // A record that only has a tenant_name is NOT configured — the user is pending approval.
    const hasAgentIds = !!(
      (integrationData.box_agent_id && integrationData.box_skill_id) ||
      (integrationData.snowflake_agent_id && integrationData.snowflake_skill_id) ||
      (integrationData.salesforce_agent_id && integrationData.salesforce_skill_id)
    );

    // Also check agent_ids_configured object from API response
    const hasAgentIdsConfigured = !!(
      integrationData.agent_ids_configured &&
      Object.values(integrationData.agent_ids_configured).some(configured => configured === true)
    );

    const isAdminConfigured = !!(workflowImported && accountConfigured && workflowSaved);
    const isUserConfigured = !!(integrationData && (hasAgentIds || hasAgentIdsConfigured) && !integrationData.is_admin);

    const isConfigured = isAdminConfigured || isUserConfigured;
    
    const result = {
      ...integrationData,
      is_configured: isConfigured,
      // Ensure workflow_status object exists for backward compatibility
      workflow_status: integrationData.workflow_status || {
        workflow_imported: workflowImported || false,
        account_configured: accountConfigured || false,
        workflow_saved: workflowSaved || false
      }
    };
    
    return result;
  } catch (error) {
    console.error('Error getting integration status:', error);
    // Check for different types of "not found" errors that should trigger setup flow
    if (error.response?.status === 404) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail === "not found" || errorDetail === "No integration configuration found") {
        return null; // Return null to trigger setup flow
      }
    }
    throw error;
  }
};

export const createOrganization = async (integrationData, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/integrations/organization`,
      {
        org_name: integrationData.tenant_name,
        box_agent_id: integrationData.box_agent_id,
        box_skill_id: integrationData.box_skill_id,
        snowflake_agent_id: integrationData.snowflake_agent_id,
        snowflake_skill_id: integrationData.snowflake_skill_id,
        salesforce_agent_id: integrationData.salesforce_agent_id,
        salesforce_skill_id: integrationData.salesforce_skill_id,
        api_token: integrationData.api_token
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

export const updateOrganization = async (orgName, token) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/api/integrations/organization`,
      { org_name: orgName },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

// Delete integration - uses DELETE /api/integrations/organization endpoint with optional target_email
export const deleteIntegration = async (token, targetEmail = null) => {
  try {
    // Validate parameters
    if (!token || typeof token !== 'string') {
      throw new Error('Invalid token provided');
    }
    
    // Ensure targetEmail is a string or null, not an object
    if (targetEmail !== null && (typeof targetEmail !== 'string' || targetEmail === '[object Object]')) {
      console.error('❌ Invalid targetEmail parameter:', targetEmail, 'Type:', typeof targetEmail);
      throw new Error('Invalid targetEmail parameter - must be a string or null');
    }
    
    const url = targetEmail 
      ? `${BASE_URL}/api/integrations/organization?target_email=${encodeURIComponent(targetEmail)}`
      : `${BASE_URL}/api/integrations/organization`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.text();
      console.error('❌ Delete request failed:', response.status, errorData);
      throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    console.log('✅ Integration deletion response:', result);
    return result;
  } catch (error) {
    console.error('❌ Error deleting integration:', error);
    throw error;
  }
};

// Legacy alias for backward compatibility - now uses the new format
export const deleteOrganization = async (orgNameOrToken, tokenOrTargetEmail = null) => {
  // Handle both old format: deleteOrganization(orgName, token) 
  // and new format: deleteOrganization(token, targetEmail)
  if (typeof orgNameOrToken === 'string' && orgNameOrToken.startsWith('eyJ')) {
    // New format: first param is token
    return deleteIntegration(orgNameOrToken, tokenOrTargetEmail);
  } else {
    // Old format: first param is orgName, second is token
    console.warn('⚠️ Using legacy deleteOrganization format. Consider using deleteIntegration instead.');
    return deleteIntegration(tokenOrTargetEmail, null);
  }
};

export const updateConfig = async (configData, token, email) => {
  try {
    const response = await axios.put(`${BASE_URL}/api/integrations/config/${email}`, configData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error updating integration config:', error);
    throw error;
  }
};

export const validateConfig = async (configData, token) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/integrations/validate`, configData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error validating integration config:', error);
    throw error;
  }
};

// getAdminTenants removed — available_tenants is now returned by GET /api/users/me/full
// Use useAdmin().availableTenants in components instead.


export const getConfig = async (email, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/integrations/config/${email}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting integration config:', error);
    throw error;
  }
};

export const createConfig = async (configData, token, email) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/integrations/config/${email}`, configData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error creating integration config:', error);
    throw error;
  }
};

// Helper functions for common deletion scenarios
export const deleteSelfIntegration = async (token) => {
  return deleteIntegration(token, null);
};

export const deleteUserIntegration = async (token, userEmail) => {
  return deleteIntegration(token, userEmail);
};

// Single save endpoint - handles complete integration setup and makes user admin
export const saveIntegration = async (integrationData, token) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/integrations/`, {
      tenant_name: integrationData.tenant_name,
      box_agent_id: integrationData.box_agent_id,
      box_skill_id: integrationData.box_skill_id,
      snowflake_agent_id: integrationData.snowflake_agent_id,
      snowflake_skill_id: integrationData.snowflake_skill_id,
      salesforce_agent_id: integrationData.salesforce_agent_id,
      salesforce_skill_id: integrationData.salesforce_skill_id,
      api_token: integrationData.api_token,
      workflow_imported: integrationData.workflow_imported || true,
      account_configured: integrationData.account_configured || true,
      workflow_saved: integrationData.workflow_saved || true
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error saving integration:', error);
    throw error;
  }
};

// Legacy alias for backward compatibility
export const createIntegration = saveIntegration;

// Update integration - uses same save endpoint to preserve admin status
export const updateIntegration = async (integrationData, token) => {
  try {
    // Use the same save endpoint for updates
    return await saveIntegration(integrationData, token);
  } catch (error) {
    console.error('Error updating integration:', error);
    throw error;
  }
};

// Legacy alias for backward compatibility
export const updateKeys = async (keys, token) => {
  return await updateIntegration(keys, token);
};



// getTenantAdmins removed — available_tenants is now returned by GET /api/users/me/full
// Use useAdmin().availableTenants in components instead.



export const downloadWorkflow = async (workflowType, token) => {
  try {
    const response = await axios.get(`${BASE_URL}/api/integrations/workflows/${workflowType}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: 'blob', // Important for file downloads
    });
    
    // Create a blob from the response data
    const blob = new Blob([response.data], { type: 'application/json' });
    
    // Create a download link and trigger it
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1].replace(/["']/g, '')
      : `${workflowType}-workflow.json`;
    
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading workflow:', error);
    if (error.response?.status === 404) {
      throw new Error('Workflow file not found');
    } else if (error.response?.status === 400) {
      throw new Error('Invalid workflow type');
    }
    throw error;
  }
};

// Select tenant - main API endpoint for when user selects a tenant
export const selectTenant = async (adminEmail, token) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/integrations/select-tenant`, 
      { admin_email: adminEmail }, 
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error selecting tenant:', error);
    throw error;
  }
};

export const clearTenant = async (token) => {
  try {
    const response = await axios.delete(`${BASE_URL}/api/integrations/clear-tenant`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    console.error('Error clearing tenant:', error);
    throw error;
  }
};